import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Modal from "../../../../components/base/Modal";
import { setPlanNotice, toShortDateWithoutTime, useHub } from "../../../../core/Global";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import SubscriptionPayDrawer from "./SubscriptionPayDrawer";
import UserSubscription from "../models/UserSubscription";
import CurrentPlan from "../models/CurrentPlan";
import CurrentPlanCard from "./CurrentPlanCard";
import PlanUpgradeDrawer from "./PlanUpgradeDrawer";
import { DrawerFormRef } from "../../../../components/drawers/DrawerForm";
import AddonsManage from "./AddonsManage";
import Div from "../../../../components/base/Div";
import Indicator from "../../../../components/base/Indicator";
import Icon from "../../../../components/base/Icon";
import PauseDrawer from "./PauseDrawer";
import ResumeDrawer from "./ResumeDrawer";
import CancelChangeDrawer from "../../../../components/plans/CancelChangeDrawer";
import SignedInDrawer from "./SignedInDrawer";
import { DrawerRef } from "../../../../components/drawers/Drawer";


export default function SubscriptionManage(props: {
    // Changes when something outside this card -- an add-on bought below it -- has changed what
    // the plan says. The card re-reads the plan quietly, without the spinner.
    refreshKey?: number
}) {

    const hub = useHub()
    const location = useLocation()
    const [fetching, fetchingSet] = useState<boolean>(true);
    const [fetched, fetchedSet] = useState<boolean>(false);
    const [plan, planSet] = useState<CurrentPlan>();
    const [model, modelSet] = useState<UserSubscription>();
    const [extraSeats, extraSeatsSet] = useState<number>(0);
    const [seatsVerifyResult, seatsVerifyResultSet] = useState<any>();

    const [subPayOpen, subPayOpenSet] = useState<boolean>(false);

    const [upgradeOpen, upgradeOpenSet] = useState<boolean>(false);
    const upgradeRef = useRef<DrawerFormRef>(null);
    // The plan an upgrade offer suggested, chosen in the drawer as it opens.
    const [upgradePlan, upgradePlanSet] = useState<string>();

    // Pausing is offered from inside the downgrade and the unsubscribe flow. The drawer it comes
    // from is closed first; this is what opens the pause once it has gone.
    const [pauseOpen, pauseOpenSet] = useState<boolean>(false);
    const [pauseAfterClose, pauseAfterCloseSet] = useState<boolean>(false);
    const pauseRef = useRef<DrawerFormRef>(null);

    const [resumeOpen, resumeOpenSet] = useState<boolean>(false);
    // Taking back a scheduled downgrade, upgrade or pause: confirmed in a drawer, the same one
    // the header's notice pill opens.
    const [cancelChangeOpen, cancelChangeOpenSet] = useState<boolean>(false);
    const cancelChangeRef = useRef<DrawerFormRef>(null);
    const resumeRef = useRef<DrawerFormRef>(null);

    const [signedInOpen, signedInOpenSet] = useState<boolean>(false);
    const signedInRef = useRef<DrawerRef>(null);

    // What the customer is on comes from the endpoint that knows about both systems: a new plan
    // if they have one, the old subscription only when they do not.
    //
    // A silent refresh leaves what is on screen alone while it runs. Raising the spinner puts a
    // block above the card and takes it away again, and the card jumps down and back -- which is
    // what closing the upgrade drawer looked like, every time, whether anything had changed or
    // not. There is nothing to wait for on a refresh: the card is already right, or about to be.
    const fetch = (silent: boolean = false) => {
        if (!silent) fetchingSet(true);

        hub.Get("/accounts/profile/plan", res => {
            const current: CurrentPlan | undefined = res.data;
            planSet(current);
            fetchingSet(false);
            fetchedSet(true);

            // The header's line follows whatever just changed here.
            hub.Get("/accounts/profile/plan/notice", nres => setPlanNotice(nres.data ?? undefined));

            // The old record is fetched only for the actions that still belong to it -- seat
            // packages, cancelling, fixing a failed charge. A customer on a new plan has none of
            // those, and asking for it would only turn up the shim record written beside theirs.
            if (current?.isLegacy) {
                hub.Get("/accounts/profile/subscription", sres => {
                    modelSet(sres.data);
                    extraSeatsSet(sres.data?.extra5SeatPackages ?? 0);
                });
            }

        });
    }

    const verify = (seats: any) => {

        hub.Post("/accounts/profile/subscription/manage/verify", {
            extraSeats: seats
        }, res => {
            if (res.success) seatsVerifyResultSet(res.data);
        });
    }

    useEffect(() => {
        fetch();
    }, [])

    // The header's "Resume" lands here with ?resume=1 and opens the resume drawer.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("resume") !== "1") return;
        params.delete("resume");
        window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
        resumeOpenSet(true);
    }, [])

    // An upgrade offer lands here with ?upgrade={plan}: the drawer opens on that plan. Read from
    // the location, so it also works when the page is already open.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get("upgrade");
        if (!code) return;
        params.delete("upgrade");
        window.history.replaceState({}, "", window.location.pathname + (params.toString() ? "?" + params.toString() : ""));
        upgradePlanSet(code);
        upgradeOpenSet(true);
    }, [location.search])

    useEffect(() => {
        if (props.refreshKey) fetch(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshKey])

    // After a 3-D Secure payment, Stripe returns to the backend which redirects here with the
    // outcome. Toast success/failure, then strip the query params so a refresh doesn't re-toast.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const payment = params.get("payment");
        if (!payment) return;
        // Add-on payments come back to this same page with kind=addon, and the add-ons card
        // below answers those. Two toasts for one payment is worse than none.
        if (params.get("kind") === "addon") return;

        if (payment === "success") toastSuccess("Your subscription payment was successful!");
        else if (payment === "failed") toastError(params.get("message") || "Your subscription payment failed.");

        params.delete("payment");
        params.delete("message");
        const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
        window.history.replaceState({}, "", clean);
    }, [])


    // A plan bought with a new card is paid for on Stripe's own page, so the outcome comes back
    // in the URL rather than in a response. Its own parameter, because the plan is switched by a
    // different endpoint from the one that answers "payment" above.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const plan = params.get("plan");
        if (!plan) return;

        if (plan === "upgraded") { toastSuccess("Your plan has been upgraded!"); fetch(true); }
        else if (plan === "failed") toastError("The payment went through but the upgrade could not be completed. Please contact us.");

        params.delete("plan");
        const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
        window.history.replaceState({}, "", clean);
    }, [])


    return (
        <>
            <div className="card mb-5">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Subscription</h3>
                    </div>
                </div>
                <div className="card-body border-top p-9">
                    <Div visible={fetching} className="my-20">
                        <Indicator />
                    </Div>
                    <Div visible={fetched && !plan?.exists}>
                        <div className="text-center py-10">
                            <Icon icon="receipt" size={36} className="text-gray-400 mb-4" />
                            <div className="fs-5 text-gray-700">No active subscription found.</div>
                        </div>
                    </Div>
                    {
                        fetched && plan?.exists &&
                        <CurrentPlanCard
                            plan={plan}
                            onChangePlan={plan.isPlan ? () => upgradeOpenSet(true) : undefined}
                            onFixPayment={plan.isLegacy ? () => subPayOpenSet(true) : undefined}
                            onUndoCancel={
                                plan.isPlan ? () => window.openModal("modal-resume-plan")
                                    : () => window.openModal("modal-undo-cancelation")
                            }
                            onCancelChange={plan.isPlan ? () => cancelChangeOpenSet(true) : undefined}
                            onResume={plan.isPlan ? () => resumeOpenSet(true) : undefined}
                            onShowSignedIn={plan.isPlan ? () => signedInOpenSet(true) : undefined}
                            onCancel={
                                plan.isPlan ? () => window.openModal("modal-unsubscribe-plan")
                                    : () => window.openModal("modal-cancel-subscription")
                            } />
                    }
                    {
                        /* Seat packages belong to the old subscription alone. When some have been
                           cancelled, the customer keeps the right to undo that until the period
                           ends, so the way back has to stay reachable. */
                        plan?.isLegacy && model?.addonsCancelText &&
                        <div className="d-flex align-items-center flex-wrap mt-5">
                            <div className="text-danger me-2">{model.addonsCancelText}</div>
                            <span className="badge badge-light-primary cursor-pointer"
                                onClick={e => {
                                    e.preventDefault();
                                    window.openModal("modal-undo-seats-cancelation");
                                }}>
                                Undo Cancellation
                            </span>
                        </div>
                    }
                </div>
            </div>

            {
                /* Add-ons are part of the plan now and are drawn inside the card above. An old
                   subscription still holds them as separate subscriptions with their own
                   renewals and cancellations, so those customers keep the card that manages
                   them until they are migrated. */
                plan?.isLegacy && <AddonsManage />
            }

            <Modal id="modal-manage-subscription"
                title="Manage Subscription"
                buttonColor="primary"
                buttonText="Update Subscription"
                confirmDisabled={!seatsVerifyResult?.verified}
                onConfirm={callback => {
                    var adding = extraSeats > (model?.extra5SeatPackages ?? 0);
                    hub.Post("/accounts/profile/subscription/manage", {
                        extraSeats: extraSeats
                    }, res => {

                        if (res.success) {
                            if (adding) {
                                document.location.href = res.data;
                            }
                            else {
                                modelSet(res.data);
                                callback();
                            }
                        }
                        else {
                            callback(true);
                        }

                    });
                }}>

                <div>
                    <div className="mb-2 mt-5 fw-bold">Extra Seats: </div>
                    <div className="mb-1">
                        <select className="form-select form-select-sm mw-100 w-300px" value={extraSeats} defaultValue={extraSeats}
                            onChange={e => {
                                var st = Number.parseInt(e.target.value);
                                extraSeatsSet(st);
                                verify(st);
                            }}>
                            {
                                [...Array(11)].map((item, key) =>
                                    <option key={key} value={key}>
                                        {key == 0 ? "No Extra Seat" : key + " Extra Package (" + (key * 5) + " Users)"}
                                    </option>
                                )
                            }
                            <option value={11}>+10 Extra Packages</option>
                        </select>
                    </div>
                    {
                        seatsVerifyResult &&
                        <>
                            <div className={"mt-4 ms-1 fw-bold fs-6 " + seatsVerifyResult?.color}>
                                {seatsVerifyResult.text}
                            </div>
                            {
                                seatsVerifyResult.adding &&
                                <div className={"mt-2 ms-1 fw-bold text-danger"}>
                                    You can't cancel it until {seatsVerifyResult.endsAt}!
                                </div>
                            }
                            {
                                seatsVerifyResult.text2 && seatsVerifyResult.text2 != "" &&
                                <div className={"mt-2 ms-1"}>
                                    {seatsVerifyResult.text2}
                                </div>
                            }
                            {
                                seatsVerifyResult.subtext && seatsVerifyResult.subtext != "" &&
                                <div className={"mt-2 ms-1"}>
                                    {seatsVerifyResult.subtext}
                                </div>
                            }



                        </>
                    }
                </div>

            </Modal>

            <Modal id="modal-cancel-subscription"
                title="Cancel Subscription"
                buttonColor="danger"
                buttonText="Confirm & Cancel Subscription"
                onConfirm={callback => {

                    hub.Post("/accounts/profile/subscription/cancel", {
                        id: model?.id
                    }, res => {
                        if (res.success) modelSet(res.data);
                        callback();
                    });

                }}>
                <div className="text-center py-6">
                    <Icon icon="circle-xmark" size={40} className="text-danger mb-5" />
                    <p className="fs-5 text-gray-800 mb-2">
                        Are you sure you want to cancel your subscription?
                    </p>
                    <p className="fs-6 text-gray-600 mb-0">
                        It stays active until <span className="fw-bold">{toShortDateWithoutTime(model?.endsAt)}</span>, then cancels automatically. You can undo it any time before then.
                    </p>
                </div>
            </Modal>

            <Modal id="modal-undo-cancelation"
                title="Undo Cancellation"
                buttonColor="primary"
                buttonText="Undo Cancellation"
                onConfirm={callback => {

                    hub.Post("/accounts/profile/subscription/undocancel", {
                        id: model?.id
                    }, res => {
                        if (res.success) modelSet(res.data);
                        callback();
                    });

                }}>
                <div className="text-center py-6">
                    <Icon icon="rotate-left" size={40} className="text-primary mb-5" />
                    <p className="fs-5 text-gray-800 mb-2">
                        Your subscription is scheduled to cancel on <span className="fw-bold">{toShortDateWithoutTime(model?.endsAt)}</span>.
                    </p>
                    <p className="fs-6 text-gray-600 mb-0">
                        Undo the cancellation to keep it active and resume automatic monthly renewals.
                    </p>
                </div>
            </Modal>

            <Modal id="modal-undo-seats-cancelation"
                title="Undo Cancellation"
                buttonColor="primary"
                buttonText="Undo Cancellation"
                onConfirm={callback => {

                    hub.Post("/accounts/profile/subscription/undoseatscancel", {
                        id: model?.id
                    }, res => {
                        if (res.success) modelSet(res.data);
                        callback();
                    });

                }}>
                <p>
                    Are you sure you want to undo the seats packages cancellation?
                </p>
            </Modal>

            {
                subPayOpen &&
                /* onSaved must not unmount it. DrawerForm calls onSubmitted and then hides the
                   drawer itself, so tearing it down here wins the race and leaves the backdrop
                   over the page. The unmount belongs in onClose, after the hide has finished. */
                <SubscriptionPayDrawer
                    amountText={model?.amountText}
                    onClose={() => subPayOpenSet(false)}
                    onSaved={() => fetch(true)} />
            }

            <Modal id="modal-unsubscribe-plan"
                title="Unsubscribe"
                buttonText="Unsubscribe" buttonColor="danger"
                onConfirm={callback => {
                    hub.Post("/accounts/profile/plan/cancel", {}, res => {
                        if (res.success) { toastSuccess("Your plan has been cancelled."); fetch(true); }
                        else toastError(res.message);
                        callback(!res.success);
                    }, () => callback(true));
                }}>
                <p className="mb-0">
                    Your {plan?.displayName ?? plan?.planName} stays exactly as it is until
                    {" "}<b>{toShortDateWithoutTime(plan?.periodEnd)}</b> — every allowance, every
                    number, every user. Nothing is charged again, and after that date the account
                    returns to the Free plan.
                </p>

                {/* The retention step, before the confirm button, as the plan document asks. */}
                <div className="plan-change-pause mt-6">
                    <div>
                        <div className="fw-bold fs-6">Not ready to leave? Pause instead</div>
                        <div className="fs-7 text-gray-700 mt-1">
                            $29 a month for up to three months. Your data and phone numbers are kept;
                            everyone can still sign in and look.
                        </div>
                    </div>
                    <button type="button" className="btn btn-sm btn-light-primary flex-shrink-0"
                        onClick={e => {
                            e.preventDefault();
                            window.closeModal("modal-unsubscribe-plan");
                            pauseOpenSet(true);
                        }}>
                        <Icon icon="pause" type="solid" size={11} className="me-2" />
                        Pause my account
                    </button>
                </div>
            </Modal>


            <Modal id="modal-resume-plan"
                title="Keep your plan"
                buttonText="Keep it"
                onConfirm={callback => {
                    hub.Post("/accounts/profile/plan/resume", {}, res => {
                        if (res.success) { toastSuccess("Your plan will renew as usual."); fetch(true); }
                        else toastError(res.message);
                        callback(!res.success);
                    }, () => callback(true));
                }}>
                <p className="mb-0">
                    Your {plan?.displayName ?? plan?.planName} will go back to renewing
                    automatically on <b>{toShortDateWithoutTime(plan?.periodEnd)}</b>.
                </p>
            </Modal>

            {
                upgradeOpen && plan &&
                <PlanUpgradeDrawer
                    plan={plan}
                    initialPlan={upgradePlan}
                    drawerRef={upgradeRef}
                    /* Closed by its own script first -- unmounting it here would win the race and
                       leave the backdrop over the page. The refetch waits for onClose. */
                    onUpgraded={() => upgradeRef.current?.close()}
                    onPauseInstead={() => { pauseAfterCloseSet(true); upgradeRef.current?.close(); }}
                    onClose={() => {
                        upgradeOpenSet(false);
                        upgradePlanSet(undefined);
                        fetch(true);
                        if (pauseAfterClose) { pauseAfterCloseSet(false); pauseOpenSet(true); }
                    }} />
            }

            {
                pauseOpen &&
                <PauseDrawer
                    drawerRef={pauseRef}
                    onPaused={() => fetch(true)}
                    onClose={() => pauseOpenSet(false)} />
            }

            {
                signedInOpen &&
                <SignedInDrawer
                    drawerRef={signedInRef}
                    refreshKey={props.refreshKey}
                    onChanged={() => fetch(true)}
                    onClose={() => signedInOpenSet(false)} />
            }

            {
                cancelChangeOpen && plan?.pendingKind &&
                <CancelChangeDrawer
                    kind={plan.pendingKind}
                    currentPlanName={plan.displayName ?? plan.planName}
                    pendingPlanName={plan.pendingPlanName}
                    effectiveAt={plan.pendingEffectiveAt}
                    drawerRef={cancelChangeRef}
                    onCancelled={() => cancelChangeRef.current?.close()}
                    onClose={() => { cancelChangeOpenSet(false); fetch(true); }} />
            }

            {
                resumeOpen && plan?.isOnHold &&
                <ResumeDrawer
                    plan={plan}
                    drawerRef={resumeRef}
                    onResumed={() => fetch(true)}
                    onClose={() => resumeOpenSet(false)} />
            }

        </>

    )
}